/**
 * NFC Solutions — Local Coordinator interface (Session 26 scaffold).
 *
 * The Local Coordinator is the LAN-level relay that distributes
 * events between every restaurant device on the same physical
 * network. It is NOT the owner of the data — every device already
 * has a complete copy. The Coordinator's job is exactly three things:
 *
 *   1. Broadcast events emitted by any device to every other device
 *      on the LAN, so the local database on Kitchen / Bar / Owner /
 *      Staff tablets converges within a few dozen milliseconds
 *      regardless of internet connectivity.
 *
 *   2. Drain each device's `event_queue` toward the cloud when
 *      internet is available, so the operator gets central backup,
 *      remote administration and multi-restaurant reconciliation.
 *
 *   3. Elect a new coordinator when the current one disappears
 *      (device unplugged, WiFi dropped). Any device on the network
 *      can take over — no restaurant operation should be interrupted.
 *
 * This file defines the INTERFACE and provides a temporary
 * cloud-relay implementation so we can wire the write path today.
 * Future sessions will add:
 *
 *   • WebRTC / WebSocket-server-per-LAN transport (real LAN broadcast)
 *   • mDNS-style discovery for coordinator election
 *   • Health-check + failover between candidate coordinators
 */

import { realtime } from "./realtime";

/**
 * The public shape of any LocalCoordinator implementation.
 *
 *   broadcast(event) → Promise<void>
 *     Send `event` to every OTHER device on this restaurant's LAN
 *     that is currently participating in this coordinator's mesh.
 *     Must NOT throw — best-effort.
 *
 *   subscribe(handler) → () => void
 *     Register a callback that fires when a peer broadcasts an
 *     event to us. Handler receives the raw envelope. Returns an
 *     unsubscribe function.
 *
 *   isLeader() → boolean
 *     Am I the elected coordinator for this LAN?
 *
 *   announce() → Promise<void>
 *     Advertise this device's presence to the LAN so peers can find
 *     me. Called periodically by the runtime.
 *
 *   status() → { role, peer_count, cloud_sync_enabled }
 *     Diagnostic snapshot for the Developer Tools card.
 */

let _instance = null;

export function setCoordinator(coordinator) {
  _instance = coordinator || null;
}

export function getCoordinator() {
  if (!_instance) _instance = makeCloudRelayCoordinator();
  return _instance;
}

// ─────────────────────────────────────────────────────────────────
// Cloud-relay coordinator — the placeholder until real LAN transport
// lands.
//
// Behaviour: every local_write() event that would normally be
// broadcast to LAN peers is instead handed to the existing cloud
// WebSocket layer, which server-echoes it to all business subscribers.
// From the app's perspective this looks identical to a LAN broadcast
// — the same reducer applies the change on every device — but it
// does traverse the cloud. That's a temporary tradeoff on the road
// to true LAN-first operation.
// ─────────────────────────────────────────────────────────────────

function makeCloudRelayCoordinator() {
  const subscribers = new Set();
  let realtimeUnsub = null;

  const ensureRealtimeSubscription = () => {
    if (realtimeUnsub) return;
    try {
      // The realtime module already dispatches messages via its own
      // subscribe(). We tap into it to surface "someone else's local
      // event" to our subscribers.
      realtimeUnsub = realtime.subscribe((msg) => {
        if (!msg || !msg.event_type) return;
        // Ignore echoes of our own events — the sender ACK'd them
        // locally already.
        if (msg._self_originated) return;
        subscribers.forEach((fn) => {
          try { fn(msg); } catch (e) { console.warn("[coordinator] subscriber error:", e?.message || e); }
        });
      });
    } catch (e) { console.warn("[coordinator] realtime subscribe error:", e?.message || e); }
  };

  return {
    kind: "cloud-relay",
    async broadcast(envelope) {
      // The cloud sync loop (eventSync.js) is what actually pushes
      // the event to /api/events/sync. That endpoint fans out the
      // change to every subscriber via WebSocket. We therefore
      // don't do anything HERE — the cloud handles fan-out.
      //
      // When real LAN transport lands, THIS is where we'd emit
      // to peers directly. Keeping the method non-empty in the
      // interface future-proofs every call site.
      return { relayed: true, kind: "cloud-relay", change_id: envelope && envelope.change_id };
    },
    subscribe(handler) {
      if (typeof handler !== "function") return () => {};
      subscribers.add(handler);
      ensureRealtimeSubscription();
      return () => subscribers.delete(handler);
    },
    isLeader() {
      // In the cloud-relay era there is no election — every device
      // talks directly to the cloud. Reporting `false` keeps future
      // leader-only UI (e.g. "this device is the coordinator" badge)
      // dormant until the real transport lands.
      return false;
    },
    async announce() {
      // No-op for cloud relay.
    },
    status() {
      return {
        role: "cloud-relay",
        peer_count: 0,
        cloud_sync_enabled: true,
        note: "LAN transport not yet enabled — every device syncs directly through the cloud.",
      };
    },
  };
}
